import React from 'react';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import { cn } from '../../lib/utils';
import { ChevronRightIcon } from '../../assets/ChevronRightIcon';
import 'dayjs/locale/en';
import 'dayjs/locale/pl';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';

dayjs.extend(localeData);
dayjs.extend(weekday);

export type CalendarView = 'days' | 'months' | 'years';

export interface CalendarRangeProps {
	initialDate?: Date;
	rangeStart?: Date;
	rangeEnd?: Date;
	onDateClick?: (date: Date) => void;
	minDate?: Date;
	maxDate?: Date;
	allowOtherMonthSelection?: boolean;
	locale?: string;
}

export function CalendarRange({
	initialDate,
	rangeStart,
	rangeEnd,
	onDateClick,
	minDate,
	maxDate,
	allowOtherMonthSelection = true,
	locale = 'en',
}: CalendarRangeProps) {
	React.useEffect(() => {
		dayjs.locale(locale);
	}, [locale]);

	const defaultDate = initialDate ?? new Date();
	const [currentDate, setCurrentDate] = React.useState(dayjs(defaultDate));
	const [currentView, setCurrentView] = React.useState<CalendarView>('days');
	const [previousView, setPreviousView] = React.useState<CalendarView>('days');

	const start = React.useMemo(
		() => (rangeStart ? dayjs(rangeStart).startOf('day') : null),
		[rangeStart],
	);
	const end = React.useMemo(
		() => (rangeEnd ? dayjs(rangeEnd).startOf('day') : null),
		[rangeEnd],
	);

	const months = React.useMemo(() => {
		return Array.from({ length: 12 }).map((_, i) => {
			const month = dayjs().locale(locale).month(i).format('MMMM');
			return month.charAt(0).toUpperCase() + month.slice(1);
		});
	}, [locale]);

	const weekdayNames = React.useMemo(() => {
		return dayjs().locale(locale).localeData().weekdaysMin();
	}, [locale]);

	const reorderedWeekdays = React.useMemo(() => {
		const days = [...weekdayNames];
		const sunday = days.shift();
		if (sunday) days.push(sunday);
		return days;
	}, [weekdayNames]);

	const getDaysInMonth = (date: dayjs.Dayjs) => date.daysInMonth();

	const getFirstDayOfMonth = (date: dayjs.Dayjs) => {
		const day = date.startOf('month').day();
		return day === 0 ? 7 : day;
	};

	const isDateInRange = (date: dayjs.Dayjs) => {
		if (minDate && date.startOf('day').isBefore(dayjs(minDate).startOf('day')))
			return false;
		if (maxDate && date.startOf('day').isAfter(dayjs(maxDate).startOf('day')))
			return false;
		return true;
	};

	const isInSelectedInterval = (date: dayjs.Dayjs) => {
		if (!start) return false;
		if (!end) return date.isSame(start, 'day');
		return (
			(date.isSame(start, 'day') ||
				date.isSame(end, 'day') ||
				(date.isAfter(start, 'day') && date.isBefore(end, 'day'))) &&
			isDateInRange(date)
		);
	};

	const isIntervalEdge = (date: dayjs.Dayjs) => {
		if (!start) return false;
		if (!end) return date.isSame(start, 'day') && isDateInRange(date);
		return (
			(date.isSame(start, 'day') || date.isSame(end, 'day')) &&
			isDateInRange(date)
		);
	};

	const isIntervalMiddle = (date: dayjs.Dayjs) => {
		if (!start || !end) return false;
		return (
			date.isAfter(start, 'day') &&
			date.isBefore(end, 'day') &&
			isDateInRange(date)
		);
	};

	const handlePrevMonth = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (currentView === 'days')
			setCurrentDate((prev) => prev.subtract(1, 'month'));
		else if (currentView === 'months')
			setCurrentDate((prev) => prev.subtract(1, 'year'));
		else setCurrentDate((prev) => prev.subtract(10, 'year'));
	};

	const handleNextMonth = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (currentView === 'days') setCurrentDate((prev) => prev.add(1, 'month'));
		else if (currentView === 'months')
			setCurrentDate((prev) => prev.add(1, 'year'));
		else setCurrentDate((prev) => prev.add(10, 'year'));
	};

	const handleMonthClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentView(currentView === 'months' ? 'days' : 'months');
	};

	const handleYearClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentView(
			currentView === 'years'
				? previousView === 'months'
					? 'months'
					: 'days'
				: 'years',
		);
		if (currentView !== 'years') setPreviousView(currentView);
	};

	const handleMonthSelect = (monthIndex: number, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentDate((prev) => prev.month(monthIndex));
		setCurrentView('days');
	};

	const handleYearSelect = (year: number, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setCurrentDate((prev) => prev.year(year));
		setCurrentView('months');
	};

	const handleDateClick = (
		day: number,
		month: number,
		year: number,
		e: React.MouseEvent,
	) => {
		e.preventDefault();
		e.stopPropagation();

		const newDate = dayjs().year(year).month(month).date(day);
		if (!isDateInRange(newDate)) return;

		onDateClick?.(newDate.toDate());
	};

	const renderCalendarDays = () => {
		const year = currentDate.year();
		const month = currentDate.month();

		const daysInMonth = getDaysInMonth(currentDate);
		const firstDayOfMonth = getFirstDayOfMonth(currentDate);

		const prevMonth = month === 0 ? 11 : month - 1;
		const prevMonthYear = month === 0 ? year - 1 : year;
		const prevMonthDate = dayjs().year(prevMonthYear).month(prevMonth);
		const daysInPrevMonth = getDaysInMonth(prevMonthDate);

		const nextMonth = month === 11 ? 0 : month + 1;
		const nextMonthYear = month === 11 ? year + 1 : year;

		const cells: React.ReactNode[] = [];

		const renderDayCell = (
			date: dayjs.Dayjs,
			dayNumber: number,
			key: string,
			onClick?: (e: React.MouseEvent) => void,
		) => {
			const isWeekend = date.day() === 0 || date.day() === 6;
			const inBounds = isDateInRange(date);

			const isEdge = isIntervalEdge(date);
			const isMiddle = isIntervalMiddle(date);
			const isSelected = isInSelectedInterval(date);

			const selectable =
				inBounds && (date.month() === month || allowOtherMonthSelection);

			return (
				<div
					key={key}
					onClick={(e) => selectable && onClick?.(e)}
					className={cn(
						'text-center w-[32px] h-[32px] flex items-center justify-center rounded-lg transition',
						isEdge ? 'bg-gradient-primary text-white cursor-pointer' : '',
						!isEdge && isMiddle ? 'bg-gray-10 text-gray-80 cursor-pointer' : '',
						!isSelected && selectable
							? 'cursor-pointer text-sm hover:bg-gray-10'
							: '',
						selectable && !isSelected && !isEdge && !isMiddle && isWeekend
							? 'text-gray-400'
							: '',
						!selectable ? 'text-gray-40 text-xs cursor-not-allowed' : '',
					)}
				>
					{dayNumber.toString().padStart(2, '0')}
				</div>
			);
		};

		// Previous month spillover
		for (let i = firstDayOfMonth - 2; i >= 0; i--) {
			const day = daysInPrevMonth - i;
			const date = dayjs().year(prevMonthYear).month(prevMonth).date(day);
			cells.push(
				renderDayCell(date, day, `prev-${day}`, (e) =>
					handleDateClick(day, prevMonth, prevMonthYear, e),
				),
			);
		}

		// Current month
		for (let day = 1; day <= daysInMonth; day++) {
			const date = dayjs().year(year).month(month).date(day);
			cells.push(
				renderDayCell(date, day, `current-${day}`, (e) =>
					handleDateClick(day, month, year, e),
				),
			);
		}

		// Next month spillover
		const totalCells = 42;
		const nextMonthDays = totalCells - cells.length;

		for (let day = 1; day <= nextMonthDays; day++) {
			const date = dayjs().year(nextMonthYear).month(nextMonth).date(day);
			cells.push(
				renderDayCell(date, day, `next-${day}`, (e) =>
					handleDateClick(day, nextMonth, nextMonthYear, e),
				),
			);
		}

		return cells;
	};

	const renderMonths = () => {
		return (
			<div className="grid grid-cols-3 gap-3 p-2">
				{months.map((m, index) => {
					const isSelected = currentDate.month() === index;

					const firstOfMonth = dayjs()
						.year(currentDate.year())
						.month(index)
						.startOf('month');
					const lastOfMonth = dayjs()
						.year(currentDate.year())
						.month(index)
						.endOf('month');

					const isInRange =
						(!minDate || lastOfMonth.isAfter(dayjs(minDate))) &&
						(!maxDate ||
							firstOfMonth.isBefore(dayjs(maxDate).endOf('day')) ||
							firstOfMonth.isSame(dayjs(maxDate), 'day'));

					return (
						<div
							key={m}
							onClick={(e) => isInRange && handleMonthSelect(index, e)}
							className={cn(
								'text-center px-1 w-full h-[32px] flex items-center justify-center rounded-lg text-sm transition',
								isSelected && isInRange
									? 'bg-gradient-primary text-white cursor-pointer'
									: '',
								isInRange && !(isSelected && isInRange)
									? 'cursor-pointer hover:bg-gray-10'
									: 'cursor-not-allowed',
							)}
						>
							{m.substring(0, 3)}
						</div>
					);
				})}
			</div>
		);
	};

	const renderYears = () => {
		const currentYear = currentDate.year();
		const startYear = Math.floor(currentYear / 10) * 10 - 1;
		const years = Array.from({ length: 12 }, (_, i) => startYear + i);

		return (
			<div className="grid grid-cols-3 gap-2 p-2">
				{years.map((y) => {
					const isSelected = currentDate.year() === y;

					const firstOfYear = dayjs().year(y).startOf('year');
					const lastOfYear = dayjs().year(y).endOf('year');

					const isInRange =
						(!minDate || lastOfYear.isAfter(dayjs(minDate))) &&
						(!maxDate ||
							firstOfYear.isBefore(dayjs(maxDate).endOf('day')) ||
							firstOfYear.isSame(dayjs(maxDate), 'year'));

					return (
						<div
							key={y}
							onClick={(e) => isInRange && handleYearSelect(y, e)}
							className={cn(
								'text-center w-full h-[32px] flex items-center justify-center rounded-lg text-sm transition',
								isSelected && isInRange
									? 'bg-gradient-primary text-white cursor-pointer'
									: '',
								y === startYear || y === startYear + 11 ? 'text-gray-400' : '',
								isInRange && !(isSelected && isInRange)
									? 'cursor-pointer hover:bg-gray-10'
									: 'cursor-not-allowed',
							)}
						>
							{y}
						</div>
					);
				})}
			</div>
		);
	};

	const renderHeader = () => {
		return (
			<div className="w-full flex items-center justify-between mb-4">
				<button
					onClick={handlePrevMonth}
					className="w-[20px] h-[20px] flex items-center justify-center p-0 m-0 rounded-md hover:bg-gray-100 border border-solid border-gray-40 bg-transparent cursor-pointer transition hover:border-gray-80"
					type="button"
				>
					<span className="flex rotate-180">
						<ChevronRightIcon />
					</span>
				</button>

				<button
					onClick={handleMonthClick}
					className={cn(
						'h-[22px] flex justify-center items-center gap-[10px] px-3 border-[1px] border-transparent border-solid bg-transparent cursor-pointer rounded-md',
						currentView === 'months'
							? 'bg-gray-100'
							: 'transition hover:border-gray-40',
					)}
					type="button"
				>
					{months[currentDate.month()]}
					<span className="flex rotate-90">
						<ChevronRightIcon />
					</span>
				</button>

				<button
					onClick={handleYearClick}
					className={cn(
						'h-[22px] flex justify-center items-center gap-[10px] px-3 border-[1px] border-transparent border-solid bg-transparent cursor-pointer rounded-md',
						currentView === 'years'
							? 'bg-gray-100'
							: 'transition hover:border-gray-40',
					)}
					type="button"
				>
					{currentDate.year()}
					<span className="flex rotate-90">
						<ChevronRightIcon />
					</span>
				</button>

				<button
					onClick={handleNextMonth}
					className="w-[20px] h-[20px] flex items-center justify-center p-0 m-0 rounded-md hover:bg-gray-100 border border-solid border-gray-40 bg-transparent cursor-pointer transition hover:border-gray-80"
					type="button"
				>
					<span className="flex">
						<ChevronRightIcon />
					</span>
				</button>
			</div>
		);
	};

	const renderCalendarContent = () => {
		switch (currentView) {
			case 'months':
				return renderMonths();
			case 'years':
				return renderYears();
			case 'days':
			default:
				return (
					<>
						<div className="grid grid-cols-7 gap-1 mb-2">
							{reorderedWeekdays.map((d) => (
								<div
									key={d}
									className="text-center text-xs font-medium text-gray-40 w-8 h-8 flex items-center justify-center"
								>
									{d.charAt(0)}
								</div>
							))}
						</div>
						<div className="grid grid-cols-7 gap-1">{renderCalendarDays()}</div>
					</>
				);
		}
	};

	return (
		<div
			className="bg-white rounded-lg shadow-md p-4 w-[280px] box-border"
			onClick={(e) => e.stopPropagation()}
		>
			{renderHeader()}
			<div className="border-0 border-solid border-t-[1px] border-gray-10" />
			{renderCalendarContent()}
		</div>
	);
}
